import { useState, useMemo, type JSX } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDeleteModel } from '../api/use-models'
import { formatRelativeTime } from '@/lib/format'
import { cn } from '@/lib/utils'
import { ArrowUpDown, Trash2 } from 'lucide-react'
import type { FilterState } from './FilterToolbar'

interface ModelRow {
  id: string
  projectId: string
  projectName: string
  name: string
  sourceType: string
  pipeCount: number
  calcStatus: string
  calcSummary: { iterations?: number; warningsCount?: number } | null
  updatedAt: string
}

type SortKey = 'name' | 'projectName' | 'calcStatus' | 'pipeCount' | 'updatedAt'
type SortDir = 'asc' | 'desc'

/** @internal exported for test — every calcStatus value must have an entry. */
export const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  converged: { label: 'Converged', color: 'text-green-500' },
  warnings: { label: 'Warnings', color: 'text-amber-500' },
  partial: { label: 'Partial', color: 'text-amber-500' },
  failed: { label: 'Failed', color: 'text-red-500' },
  none: { label: 'Not calculated', color: 'text-muted-foreground' },
}

interface ModelsTableViewProps {
  models: ModelRow[]
  filters: FilterState
}

export function ModelsTableView({ models, filters }: ModelsTableViewProps): JSX.Element {
  const navigate = useNavigate()
  const deleteModel = useDeleteModel()
  const [sortKey, setSortKey] = useState<SortKey>('updatedAt')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const filtered = useMemo((): Array<ModelRow> => {
    let result = models
    if (filters.search) {
      const q = filters.search.toLowerCase()
      result = result.filter((m): boolean =>
        m.name.toLowerCase().includes(q) || m.projectName.toLowerCase().includes(q),
      )
    }
    if (filters.projectId) {
      result = result.filter((m): boolean => m.projectId === filters.projectId)
    }
    if (filters.calcStatus) {
      result = result.filter((m): boolean => (m.calcStatus || 'none') === filters.calcStatus)
    }
    return result
  }, [models, filters])

  const sorted = useMemo((): Array<ModelRow> => {
    return [...filtered].sort((a, b): number => {
      let cmp = 0
      switch (sortKey) {
        case 'name': cmp = a.name.localeCompare(b.name); break
        case 'projectName': cmp = a.projectName.localeCompare(b.projectName); break
        case 'calcStatus': cmp = (a.calcStatus || 'none').localeCompare(b.calcStatus || 'none'); break
        case 'pipeCount': cmp = a.pipeCount - b.pipeCount; break
        case 'updatedAt': cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(); break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filtered, sortKey, sortDir])

  const toggleSort = (key: SortKey): void => {
    if (sortKey === key) setSortDir((d): "asc" | "desc" => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(key); setSortDir('asc') }
  }

  const handleDelete = (model: ModelRow, e: React.MouseEvent): void => {
    e.stopPropagation()
    if (deletingId === model.id) {
      deleteModel.mutate({ projectId: model.projectId, modelId: model.id })
      setDeletingId(null)
    } else {
      setDeletingId(model.id)
    }
  }

  const cancelDelete = (e: React.MouseEvent): void => { e.stopPropagation(); setDeletingId(null) }

  const headerClasses = 'px-3 py-2 text-left text-xs font-semibold text-muted-foreground cursor-pointer select-none hover:text-foreground transition-colors'

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            {(['name', 'projectName', 'calcStatus', 'pipeCount', 'updatedAt'] as SortKey[]).map((key): JSX.Element => {
              const labels: Record<SortKey, string> = { name: 'Name', projectName: 'Project', calcStatus: 'Status', pipeCount: 'Pipes', updatedAt: 'Last Modified' }
              return (
                <th key={key} className={headerClasses} onClick={() => toggleSort(key)}>
                  <span className="flex items-center gap-1">
                    {labels[key]}
                    <ArrowUpDown className="h-3 w-3" />
                  </span>
                </th>
              )
            })}
            <th className="px-3 py-2 text-right text-xs font-semibold text-muted-foreground">Actions</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr><td colSpan={6} className="px-3 py-8 text-center text-sm text-muted-foreground">
              {filters.search || filters.projectId || filters.calcStatus ? 'No models match the current filters.' : 'No models yet.'}
            </td></tr>
          ) : sorted.map((model): JSX.Element => {
            const statusConfig = STATUS_LABELS[model.calcStatus || 'none'] ?? { label: 'Not calculated', color: 'text-muted-foreground' }
            const isDeleting = deletingId === model.id
            return (
              <tr key={model.id} onClick={() => navigate(`/projects/${model.projectId}/models/${model.id}`)} className="cursor-pointer border-t transition-colors hover:bg-muted/30">
                <td className="px-3 py-2"><span className="font-medium">{model.name}</span></td>
                <td className="px-3 py-2 text-muted-foreground">{model.projectName}</td>
                <td className="px-3 py-2"><span className={cn('text-xs font-medium', statusConfig.color)}>{statusConfig.label}</span></td>
                <td className="px-3 py-2 font-mono text-muted-foreground">{model.pipeCount}</td>
                <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(model.updatedAt)}</td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    {isDeleting ? (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button onClick={(e) => handleDelete(model, e)} className="rounded bg-red-600 px-2 py-0.5 text-[10px] text-white hover:bg-red-700">Confirm</button>
                        <button onClick={cancelDelete} className="rounded bg-muted px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-muted/80">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={(e) => handleDelete(model, e)} className="rounded p-1 text-muted-foreground hover:bg-red-500/10 hover:text-red-500 transition-colors" title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
